/**
 * Auth context for the Patient portal.
 * - null: still checking session
 * - {user}: authenticated
 * - false: not authenticated
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { patientApi } from "@/lib/patientApi";

const PatientAuthContext = createContext(null);

export function PatientAuthProvider({ children }) {
  const [state, setState] = useState(null); // null | false | { user }

  const refresh = useCallback(async () => {
    try {
      const { user } = await patientApi.me();
      setState({ user });
      return user;
    } catch (e) {
      setState(false);
      return null;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async ({ email, password }) => {
    const data = await patientApi.login({ email, password });
    setState({ user: data.user });
    return data.user;
  };

  const register = async (b) => {
    const data = await patientApi.register(b);
    setState({ user: data.user });
    return data.user;
  };

  const logout = async () => {
    try { await patientApi.logout(); } catch (e) { /* ignore */ }
    setState(false);
  };

  const setUser = (user) => setState({ user });

  return (
    <PatientAuthContext.Provider value={{ state, login, register, logout, refresh, setUser }}>
      {children}
    </PatientAuthContext.Provider>
  );
}

export function usePatientAuth() {
  const ctx = useContext(PatientAuthContext);
  if (!ctx) throw new Error("usePatientAuth must be used within PatientAuthProvider");
  return ctx;
}
